/**
 * The ONE owner of the agent-posture read and the landing write (D-367).
 *
 * SPEC-FRONTEND-RULE-077 and the D-226 queryOptions ruling: one key, one
 * fetcher, exported as queryOptions so every consumer shares them rather than
 * declaring its own queryFn against the same key. Two surfaces read this
 * resource - the tenant settings card here and the agents page's create
 * affordance (TASK-GOV-CAP-TENANT-FE) - and a second fetcher on
 * ["me","agent-posture"] would mean whichever mounted first decided what the
 * other saw, which is exactly the defect that emptied the admin model picker
 * after a visit to Settings (docs/research/spread-scan-20260904-092236).
 *
 * @module api/agentPosture
 */
import { queryOptions } from "@tanstack/react-query";

import client from "./client";

/** The two landing values, mirroring repository/agent_landing.go. */
export type AgentLanding = "chooser" | "agent";

/**
 * GET /api/v1/me/agent-posture (SPEC-GO-API-CONTRACT-047).
 *
 * The three questions a tenant surface has before it renders: may this tenant
 * hold more than one agent, may THIS user create one, and where should opening
 * the surface put them.
 *
 * There is deliberately no client-side default for this type. The gateway
 * answers 503 rather than guessing when it cannot read any leg, so a consumer
 * that substituted a default here would re-introduce exactly the guess the
 * server refuses to make.
 */
export interface AgentPosture {
  multi_agent_allowed: boolean;
  may_create: boolean;
  /** Resolved for THE CALLER since D-375 (CONTRACT-047 as amended). */
  agent_landing: AgentLanding;
  agent_landing_id: string | null;
  /** Whose preference decided: the caller's own per-user landing, or the
   *  tenant default. */
  agent_landing_source: AgentLandingSource;
  /** The tenant default, always present, so the landing chain can fall back
   *  to it when the caller's own agent is not usable. */
  tenant_agent_landing: AgentLanding;
  tenant_agent_landing_id: string | null;
}

/** Whose landing preference the posture reports (D-375). */
export type AgentLandingSource = "user" | "tenant";

export function agentPostureQueryKey() {
  return ["me", "agent-posture"] as const;
}

export async function fetchAgentPosture(): Promise<AgentPosture> {
  const { data } = await client.get<AgentPosture>("/me/agent-posture");
  return data;
}

export function agentPostureQueryOptions() {
  return queryOptions({
    queryKey: agentPostureQueryKey(),
    queryFn: fetchAgentPosture,
  });
}

/** The PUT body of SPEC-GO-API-CONTRACT-048 - exactly these two keys. */
export interface AgentLandingBody {
  agent_landing: AgentLanding;
  agent_landing_id: string | null;
}

/**
 * PUT /api/v1/tenant/agent-landing (SPEC-GO-API-CONTRACT-048).
 *
 * The body is built HERE, from the two declared fields only. The gateway
 * decodes with DisallowUnknownFields because this settings row is shared with
 * chat_history_retention_days, so a stray key is a 400 rather than a silent
 * drop - spreading a component's form state into this call would turn a UI
 * refactor into a failing save.
 */
export async function putAgentLanding(body: AgentLandingBody): Promise<AgentLandingBody> {
  const { data } = await client.put<AgentLandingBody>("/tenant/agent-landing", {
    agent_landing: body.agent_landing,
    agent_landing_id: body.agent_landing_id,
  });
  return data;
}

/** One agent as the landing picker needs it. */
export interface TenantAgentOption {
  id: string;
  name: string;
}

/**
 * The picker's option list.
 *
 * Keyed under the "agents" PREFIX on purpose: the tenant agents page already
 * calls `invalidateQueries({ queryKey: ["agents"] })` after a create or a
 * delete, so this list refreshes with it and a just-created agent is
 * immediately choosable. The second segment is a literal no existing
 * ["agents", <id>] key uses, so prefix invalidation reaches it without this
 * key ever colliding with a single-agent query.
 */
export function tenantAgentOptionsQueryKey() {
  return ["agents", "landing-options"] as const;
}

/** The page size the gateway defaults to; asked for explicitly so the paging
 *  arithmetic below does not depend on a server default staying put. */
const AGENT_PAGE_SIZE = 100;

/**
 * Every agent of the tenant, not the first page of them.
 *
 * GET /api/v1/agents is paginated (handleListAgents), so a single request
 * returns a page and a total. Presenting that first page as the whole list
 * would silently hide agents from the picker - the tenant would be unable to
 * choose one it can see elsewhere in the product, with no error to explain it.
 * So this follows pages until it has total_count of them, with a hard stop so
 * a server that reports a total it never delivers cannot spin forever.
 */
export async function fetchTenantAgentOptions(): Promise<TenantAgentOption[]> {
  interface AgentPage {
    items?: Array<{ id: string; name: string }>;
    total_count?: number;
  }

  const out: TenantAgentOption[] = [];
  let page = 1;
  // Bounded: at 100 per page this is 10 000 agents, far past any real tenant,
  // and it makes a bad total_count a truncated list rather than a hung tab.
  const maxPages = 100;

  for (; page <= maxPages; page++) {
    const { data } = await client.get<AgentPage>("/agents", {
      params: { page, per_page: AGENT_PAGE_SIZE },
    });
    const items = data?.items ?? [];
    for (const a of items) {
      out.push({ id: a.id, name: a.name });
    }
    const total = data?.total_count ?? out.length;
    if (items.length === 0 || out.length >= total) {
      break;
    }
  }
  return out;
}

export function tenantAgentOptionsQueryOptions() {
  return queryOptions({
    queryKey: tenantAgentOptionsQueryKey(),
    queryFn: fetchTenantAgentOptions,
  });
}

// ---------------------------------------------------------------------------
// Per-user landings (D-375, SPEC-GO-API-RULE-051, CONTRACT-049/050/051)
// ---------------------------------------------------------------------------

/** One row of CONTRACT-049 and the 200 body of CONTRACT-050. */
export interface UserAgentLanding {
  user_id: string;
  agent_landing: AgentLanding;
  agent_landing_id: string | null;
}

/**
 * The per-user list's ONE key and fetcher (RULE-077). Under the "tenant" prefix,
 * never under ["me", ...]: it is the tenant's list, not the caller's posture.
 */
export function userAgentLandingsQueryKey() {
  return ["tenant", "user-agent-landings"] as const;
}

/** GET /api/v1/tenant/users/agent-landing (CONTRACT-049). A lookup failure is a
 *  503 the caller must show, never an empty list. */
export async function listUserAgentLandings(): Promise<UserAgentLanding[]> {
  const { data } = await client.get<{ items?: UserAgentLanding[] }>(
    "/tenant/users/agent-landing",
  );
  return data?.items ?? [];
}

export function userAgentLandingsQueryOptions() {
  return queryOptions({
    queryKey: userAgentLandingsQueryKey(),
    queryFn: listUserAgentLandings,
  });
}

/**
 * PUT /api/v1/tenant/users/{uid}/agent-landing (CONTRACT-050).
 *
 * The body is built HERE from exactly the two declared keys, as putAgentLanding
 * does: the gateway decodes with DisallowUnknownFields, and a tenant id is never
 * sent - the server takes it from the verified token.
 */
export async function putUserAgentLanding(
  userId: string,
  body: AgentLandingBody,
): Promise<UserAgentLanding> {
  const { data } = await client.put<UserAgentLanding>(
    `/tenant/users/${encodeURIComponent(userId)}/agent-landing`,
    {
      agent_landing: body.agent_landing,
      agent_landing_id: body.agent_landing === "agent" ? body.agent_landing_id : null,
    },
  );
  return data;
}

/** DELETE /api/v1/tenant/users/{uid}/agent-landing (CONTRACT-051): the user
 *  follows the tenant default again. Idempotent on the server. */
export async function deleteUserAgentLanding(userId: string): Promise<void> {
  await client.delete(`/tenant/users/${encodeURIComponent(userId)}/agent-landing`);
}

/** One user of the tenant, as the per-user landing control shows it. */
export interface TenantUserOption {
  id: string;
  email: string;
  name: string;
}

/**
 * The tenant's users, for the per-user picker and for naming the listed rows.
 *
 * GET /api/v1/tenant/users (the same scoped search searchTenantUsers calls) is
 * paginated, so this follows pages until it has total_count of them - a first
 * page presented as the whole tenant would leave users unpickable and show
 * others as bare ids with no error to explain it. Its own key: nothing else
 * caches this endpoint.
 */
export function tenantUserOptionsQueryKey() {
  return ["tenant", "users", "landing-options"] as const;
}

const USER_PAGE_SIZE = 100;

export async function fetchTenantUserOptions(): Promise<TenantUserOption[]> {
  interface UserPage {
    items?: TenantUserOption[];
    total_count?: number;
  }
  const out: TenantUserOption[] = [];
  // Bounded like fetchTenantAgentOptions: a server whose total_count is never
  // delivered becomes a truncated list, not a hung tab.
  const maxPages = 100;
  for (let page = 1; page <= maxPages; page++) {
    const { data } = await client.get<UserPage>("/tenant/users", {
      params: { page, per_page: USER_PAGE_SIZE },
    });
    const items = data?.items ?? [];
    out.push(...items);
    const total = data?.total_count ?? out.length;
    if (items.length === 0 || out.length >= total) {
      break;
    }
  }
  return out;
}

export function tenantUserOptionsQueryOptions() {
  return queryOptions({
    queryKey: tenantUserOptionsQueryKey(),
    queryFn: fetchTenantUserOptions,
  });
}
